/**
 * Runtime detection: где сейчас работает WebApp — внутри Telegram Mini App
 * или в браузере на app.zero-wallet.net.
 *
 * `telegram-web-app.js` создаёт `window.Telegram.WebApp` в любом окружении,
 * но `initData` заполняется ТОЛЬКО когда мини-апп открыт из Telegram-бота.
 * Пустая строка = мы в браузере.
 *
 * Значение вычисляется один раз при загрузке модуля — за время работы SPA
 * оно не меняется (юзер не «телепортируется» из TG в браузер, и наоборот).
 */

export type Runtime = "telegram" | "web";

function detectRuntime(): Runtime {
  if (typeof window === "undefined") return "web"; // SSR-safety.
  try {
    const initData = window.Telegram?.WebApp?.initData;
    return initData && initData.length > 0 ? "telegram" : "web";
  } catch {
    return "web";
  }
}

export const RUNTIME: Runtime = detectRuntime();

export const IS_TELEGRAM = RUNTIME === "telegram";
export const IS_WEB = RUNTIME === "web";

/**
 * Prod / dev режим фронта. Управляется переменной окружения
 * `REACT_APP_PROD_MODE` (см. alfin-app/.env).
 *
 *   • prod  = true  → включена веб-авторизация (email OTP + PIN), отдельный
 *                     домен app.zero-wallet.net, всё «взрослое».
 *   • prod  = false → dev-режим через ngrok: фронт крутится «как было»,
 *                     WebAuthScreen пропускается, даже если IS_WEB (например,
 *                     когда мы открываем ngrok-ссылку в браузере отдельно
 *                     от Telegram — просто чтобы посмотреть вёрстку).
 */
export const IS_PROD_MODE = process.env.REACT_APP_PROD_MODE === "true";

export const ASSET_VERSION = process.env.REACT_APP_VERSION || "0.1";

/** Добавляет `?v=<ASSET_VERSION>` к пути статического ассета для сброса кэша в TG WebApp. */
export const v = (path: string) => `${path}?v=${ASSET_VERSION}`;

/**
 * Безопасный доступ к Telegram-объекту. В веб-режиме возвращает `null`.
 * Использовать вместо прямого `Telegram.WebApp.X` в местах, где логика
 * должна работать и в браузере (haptic, BackButton, showAlert и т.п.).
 *
 * Если тебе нужен объект в TG-режиме и ты уверен, что он есть — просто
 * оставь прямой доступ; но всё, что вызывается на *общем* пути (layout,
 * auth) должно ходить через `tg()` и проверять на null.
 */
export function tg(): NonNullable<Window["Telegram"]>["WebApp"] | null {
  if (!IS_TELEGRAM) return null;
  try {
    return window.Telegram?.WebApp ?? null;
  } catch {
    return null;
  }
}

/**
 * No-op заглушки, чтобы веб-режим не падал при вызовах Telegram-API.
 * Используются в компонентах, которые не хочется ветвить сплошным
 * `if (IS_TELEGRAM)`.
 */
export const tgHaptic = {
  impactOccurred: (
    style: "light" | "medium" | "heavy" | "rigid" | "soft" = "light",
  ) => {
    tg()?.HapticFeedback?.impactOccurred(style);
  },
  notificationOccurred: (
    type: "error" | "success" | "warning",
  ) => {
    tg()?.HapticFeedback?.notificationOccurred(type);
  },
  selectionChanged: () => {
    tg()?.HapticFeedback?.selectionChanged();
  },
};
